/* eslint-disable @typescript-eslint/no-explicit-any */
// Opening a lens, and the three best-effort enhancements around it.
//
// PROMOTED 2026-09-30 from modules/lib/src/fe/scanner/camera.ts, unchanged in
// behaviour. `@erp/ui` cannot import a module (Fig 4.1), and the capture sheet
// here needs exactly the torch and focus helpers the barcode scanner already
// had — so the definition moves down to the package and the scanner re-exports
// it from its own barrel. One definition, and no call site in lib changed.
//
// focusMode/torch live in the W3C mediacapture-image spec and are typed nowhere
// in lib.dom (hence the anys) — in practice Android Chrome exposes them, iOS
// Safari does not (its lenses run continuous AF natively and no web torch
// exists there). Every helper is hardened to NEVER throw: the portal test
// suites hand these fake streams.

/** Ask the lens to keep re-focusing on the subject instead of drifting to the
 * background — a no-op wherever the capability is absent. */
export async function applyContinuousFocus(stream: MediaStream | null): Promise<void> {
  try {
    const track = stream?.getVideoTracks?.()[0];
    const caps = track?.getCapabilities?.() as any;
    if (!caps || !Array.isArray(caps.focusMode) || !caps.focusMode.includes('continuous')) return;
    await track!.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] });
  } catch { /* best-effort */ }
}

/** Whether this track can drive the flashlight (Android Chrome, mostly). */
export function torchCapable(stream: MediaStream | null): boolean {
  try {
    const track = stream?.getVideoTracks?.()[0];
    return Boolean((track?.getCapabilities?.() as any)?.torch);
  } catch { return false; }
}

/** Switch the flashlight; resolves false when the device refused or can't. */
export async function setTorch(stream: MediaStream | null, on: boolean): Promise<boolean> {
  try {
    const track = stream?.getVideoTracks?.()[0];
    if (!(track?.getCapabilities?.() as any)?.torch) return false;
    await track!.applyConstraints({ advanced: [{ torch: on } as any] });
    return true;
  } catch { return false; }
}

/** Which lens to ask for. `environment` is the rear camera — what a book is under. */
export type Facing = 'environment' | 'user';

/**
 * Is there a camera to open at all?
 *
 * Deliberately a CAPABILITY test and not a user-agent sniff: a laptop with a
 * webcam should get the button and a phone with the permission already denied
 * should still get it (pressing it is how a reader re-grants). It is also false
 * on plain HTTP, because getUserMedia does not exist outside a secure context —
 * which is why the dev server runs with DEV_HTTPS=1.
 */
export function hasCamera(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * Open a lens, preferring `facing`, and fall back to ANY camera rather than
 * failing.
 *
 * `facingMode` as an EXACT constraint is refused outright by a laptop with one
 * front-facing webcam, and by a surprising number of Android tablets whose rear
 * lens reports no facing at all. Asking for it as a preference and retrying
 * bare is the difference between "capture works everywhere" and "capture works
 * on phones we tested".
 *
 * The resolution ask is a PREFERENCE too (ideal, never exact): a lens that
 * cannot do 1920 wide gives what it has, and the pipeline downscales from
 * whatever arrives.
 */
export async function openCamera(facing: Facing = 'environment'): Promise<MediaStream> {
  if (!hasCamera()) throw new Error('This device has no camera available to the browser.');
  const ideal: MediaStreamConstraints = {
    video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1440 } },
    audio: false,
  };
  try {
    return await navigator.mediaDevices.getUserMedia(ideal);
  } catch (error) {
    // A refusal is the reader saying no — do not retry it as a different
    // constraint, which just asks twice and reads as nagging.
    if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) throw error;
    return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
  }
}

/** Stop every track so the lens light goes out. Safe on null and on a stopped stream. */
export function stopStream(stream: MediaStream | null | undefined): void {
  try {
    for (const track of stream?.getTracks?.() ?? []) track.stop();
  } catch { /* already gone */ }
}
